import { Command, Store, TimeOffEvent } from '@nextforce/api'
import { Text, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import { groupBy } from 'lodash'
import { useMemo } from 'react'
import { SectionList, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { TimeOffRequestCard } from './card'

interface Props {
  timeOffEvents: TimeOffEvent[] | undefined
  onButtonPress: (command: Command, timeOffEvent: TimeOffEvent) => Promise<void>
  stores: Store[] | undefined
}

interface Section {
  sectionTitle: string
  data: TimeOffEvent[]
  index?: number
}

export function TimeOffRequestListView({
  timeOffEvents,
  onButtonPress,
  stores,
}: Props) {
  const styles = useStyles()

  const sections: Section[] = useMemo(() => {
    if (!timeOffEvents?.length) {
      return []
    }
    const groupedByStoreId = groupBy(
      timeOffEvents,
      timeOffEvent => timeOffEvent.storeId
    )
    const allSections: Section[] = []
    const storeIds = Object.keys(groupedByStoreId)
    storeIds.forEach(storeId => {
      const store = stores?.find(s => s.id === storeId)
      const groupedByStatus = groupBy(groupedByStoreId[storeId], event =>
        event.status.toString()
      )
      const section: Section = {
        sectionTitle: `Store ${store?.storeNumber} - ${store?.name}`,
        data: [],
      }
      const statusKeys = Object.keys(groupedByStatus)
      if (statusKeys.includes('PENDING')) {
        section.data.push(...groupedByStatus['PENDING'])
      }
      if (statusKeys.includes('APPROVED')) {
        section.data.push(...groupedByStatus['APPROVED'])
      }
      if (statusKeys.includes('REJECTED')) {
        section.data.push(...groupedByStatus['REJECTED'])
      }
      allSections.push(section)
    })
    return allSections
  }, [stores, timeOffEvents])

  if (!sections.length) {
    return (
      <View style={styles.noTimeOffContainer}>
        <Text style={styles.noTimeOffTitle}>{t('noNewTimeOffRequests')}</Text>
      </View>
    )
  }

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <SectionList
          sections={sections}
          contentContainerStyle={{ paddingBottom: 10 }}
          data={timeOffEvents}
          renderSectionHeader={({ section }) => {
            return <Text style={styles.header}>{section.sectionTitle}</Text>
          }}
          stickySectionHeadersEnabled
          renderItem={({ item }) => {
            return (
              <TimeOffRequestCard
                timeOffEvent={item}
                onButtonPress={onButtonPress}
              />
            )
          }}
        />
      </View>
    </SafeAreaView>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: { flex: 1, backgroundColor: theme.colors.grey6 },
  header: {
    padding: 10,
    flex: 1,
    fontSize: 20,
    fontWeight: 'bold',
    backgroundColor: theme.colors.white,
    elevation: 5,
  },
  noTimeOffContainer: {
    flex: 1,
    backgroundColor: theme.colors.grey6,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 50,
  },
  noTimeOffTitle: {
    fontSize: 24,
    textAlign: 'center',
  },
}))
